<script setup lang="ts">
import { computed } from 'vue'
import { LOG_KINDS, linkifyLogText, logTime, type BrainLogEntry } from '../composables/parseBrainLog'

// One activity-log entry: local time, an operation pill, the headline
// and its detail. Wiki paths in the text link to the page when it still
// exists; a path the catalog no longer has (e.g. a deleted page) stays
// plain mono text.

const props = defineProps<{
  entry: BrainLogEntry
  knownPaths: Set<string>
}>()

const kind = computed(() => LOG_KINDS[props.entry.kind])
const titleSegments = computed(() => linkifyLogText(props.entry.title))
const detailSegments = computed(() => linkifyLogText(props.entry.detail))
</script>

<template>
  <article
    class="grid grid-cols-[48px_minmax(0,1fr)] gap-3.5 border-t border-border-divider py-3.5 sm:grid-cols-[56px_minmax(0,1fr)]"
    data-testid="log-entry"
  >
    <time :datetime="entry.at.toISOString()" class="pt-[3px] font-mono text-xs text-ink-faint">
      {{ logTime(entry.at) }}
    </time>
    <div class="flex min-w-0 flex-col gap-1.5">
      <div class="flex flex-wrap items-center gap-2">
        <span class="rounded-full px-2 py-0.5 text-xs font-semibold" :class="kind.tone" data-testid="log-entry-kind">
          {{ kind.label }}
        </span>
        <span class="min-w-0 break-words font-medium text-ink">
          <template v-for="(seg, i) in titleSegments" :key="i">
            <RouterLink
              v-if="seg.path && knownPaths.has(seg.path)"
              :to="`/p/${seg.path}`"
              class="font-mono text-[13px] text-brand-primary underline underline-offset-2"
              >{{ seg.text }}</RouterLink
            ><span v-else :class="{ 'font-mono text-[13px]': seg.path }">{{ seg.text }}</span>
          </template>
        </span>
      </div>
      <p v-if="entry.detail" class="m-0 break-words leading-normal text-ink-muted">
        <template v-for="(seg, i) in detailSegments" :key="i">
          <RouterLink
            v-if="seg.path && knownPaths.has(seg.path)"
            :to="`/p/${seg.path}`"
            class="font-mono text-xs text-brand-primary underline underline-offset-2"
            >{{ seg.text }}</RouterLink
          ><span v-else :class="{ 'font-mono text-xs': seg.path }">{{ seg.text }}</span>
        </template>
      </p>
    </div>
  </article>
</template>
